import type { ReactNode } from 'react'
import { ChannelMark } from '@/components/channel-icons'
import { CALENDAR_COLOR_CLASSES } from '@/components/calendar/month-grid'
import { cn } from '@/lib/utils'

/**
 * The calendar legend's entries, shared by the property Calendar tab and the
 * portfolio calendar (CAP-1417), so a colour or a swatch means the same thing
 * on both.
 */

const CALENDAR_COLOR_LABELS: Record<string, string> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  direct: 'Direct',
  pending: 'Pending / inquiry',
  unknown: 'Unknown',
}

/**
 * Always shown in the legend, regardless of what is booked on screen, so the
 * colour key is a stable reference rather than shifting with the dates.
 * "unknown" is left out (a fallback bucket, not a real category) unless a
 * screen draws a stay in it.
 */
const ALWAYS_SHOWN_LEGEND_KEYS = ['airbnb', 'vrbo', 'direct', 'pending']

/** A stay's colour by its channel, the channel's own mark where it has one: one entry per channel. */
export function ChannelLegend({ withUnknown = false }: { withUnknown?: boolean }) {
  const keys = withUnknown ? [...ALWAYS_SHOWN_LEGEND_KEYS, 'unknown'] : ALWAYS_SHOWN_LEGEND_KEYS
  return (
    <>
      {keys.map(colorKey => (
        <div key={colorKey} className="flex items-center gap-1.5">
          {colorKey === 'airbnb' || colorKey === 'vrbo' ? (
            <ChannelMark channel={colorKey} className="h-3 w-3" />
          ) : (
            <span
              className={cn('h-2.5 w-2.5 rounded-sm border', CALENDAR_COLOR_CLASSES[colorKey])}
              aria-hidden="true"
            />
          )}
          <span>{CALENDAR_COLOR_LABELS[colorKey]}</span>
        </div>
      ))}
    </>
  )
}

/** A bar's look, as a small swatch beside its name: a block, a hold, empty nights. */
export function LegendSwatch({ className, children }: { className: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn('h-2.5 w-2.5 rounded-sm border', className)} aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}
